<template>
  <b-card :title="item.name"
          class="meal-card"
          v-on:click="onClick"
          :class="cssClass">

    <div slot="header" class="meal-card__image">
      <img :src="itemImage(item)" :alt="item.name" class="img-fluid"/>
    </div>

    <p class="card-text">{{ item.description }}</p>

    <div slot="footer">
      <div class="ingredients">
        <span v-for="ingredient in ingredients" :key="ingredient">
          <IngredientIcon :ingredient="ingredient" />
          {{ ingredient | capitalize }}
        </span>
      </div>
      <div class="price">
        {{ item.price / 100 | currency }}
      </div>
      <div class="ratings" v-if="item.hasOwnProperty('averageRating')">
        U: {{ userRating(item) || '-'}}
        A: {{ item.averageRating || '-'}}
      </div>
    </div>
  </b-card>
</template>

<script>
import NoImage from 'forkable/assets/meal-no-image.png'
import IngredientIcon from './ingredient-icon'

const INGREDIENTS = ['dairy', 'gluten', 'nuts', 'soy', 'seafood']

export default {
  components: {IngredientIcon},
  props: {
    item: { type: Object, default: {} },
    selected: { type: Boolean, default: false },
    selectable: { type: Boolean, default: true },
    filtered: { type: Boolean, default: false },
    forUser: { type: Object, default: null },
    ingredientMap: { type: Array, required: true },
  },

  data() {
    const ingredients = this.ingredientMap.map(map => {
      if (!this.item.ingredients) return null

      const visible = INGREDIENTS.indexOf(map.category) >= 0
      if (visible && map.ingredients.find(ingredient => this.item.ingredients.includes(ingredient))) {
        return map.category
      } else {
        return null
      }
    })
      .filter(ingredient => !!ingredient)
      .sort((a, b) => INGREDIENTS.indexOf(a) < INGREDIENTS.indexOf(b) ? -1 : 1)

    return {
      ingredients,
    }
  },

  computed: {
    cssClass() {
      return {
        'meal-card--selected': this.selected,
        'meal-card--filtered': this.filtered,
        'meal-card--selectable': this.selectable,
      }
    }
  },

  methods: {
    userRating(item) {
      if (!this.forUser) return null
      return (item.userRatings.find(r => r.userId === this.forUser.id) || {}).rating
    },

    itemImage(item) {
      return item.imageUrl || NoImage
    },

    onClick() {
      if (this.selectable) this.$emit('select')
    }
  },
}
</script>

<style lang="scss">
.meal-card {
  border: 0;
  border-bottom: $card-border-width solid $card-border-color;

  .card-header {
    padding: 0;
    background-color: transparent;
    border: 0;
  }

  .card-footer {
    background-color: transparent;
    border: 0;
    font-size: $font-size-xs;
  }

  .price {
    float: left;
    color: $secondary;
    font-size: $font-size-sm;
    font-weight: bold;
  }

  .ratings {
    float: right;
    color: $primary;
    font-size: $font-size-sm;
    font-weight: bold;
  }

  h4 {
    font-size: $font-size-sm;
    line-height: 19px;
  }

  .card-text {
    font-size: $font-size-xs;
    line-height: 22px;
  }

  .ingredients {
    margin-bottom: 1rem;
    @include clearfix;

    span {
      display: block;
      float: left;
      width: 45px;
      text-align: center;
      color: #000;
    }

    svg {
      display: block;
      margin: 0 auto 0.5rem auto;
      max-height: 18px;
    }
  }
}

.meal-card__image {
  position: relative;
  background-color: $gray-500;
  width: 480px;
  height: auto;
  max-height: 320px;
  max-width: 100%;

  &:before {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba($primary, 0.7);
    pointer-events: none;
  }
}

.meal-card--selected:not(:hover) {
  &:before {
    position: absolute;
    content: "";
    width: calc(100% + 10px);
    height: calc(100% + 7px);
    left: -5px;
    top: -5px;
    border: 3px solid $primary;
    pointer-events: none;
  }

  &:after {
    position: absolute;
    content: "selected";
    font-variant: small-caps;
    left: 15px;
    top: -10px;
    background-color: $primary;
    color: #fff;
    line-height: 1;
    padding: 0 5px;
  }
}

.meal-card--selectable {
  cursor: pointer;

  &:hover {
    &:before {
      position: absolute;
      content: "";
      width: 100%;
      height: 100%;
      border: 1px solid $primary;
      pointer-events: none;
    }

    .meal-card__image:before {
      content: "";
    }

    .meal-card__image:after {
      content: "Select Meal";
      text-align: center;
      position: absolute;
      width: 100%;
      left: 0;
      top: 50%;
      margin-top: -0.5em;
      color: #fff;
      pointer-events: none;
    }
  }
}

.meal-card--selected.meal-card--filtered {
  color: $gray-500;

  &:before {
    border-color: $gray-300;
  }

  &:after {
    color: $gray-500;
    background-color: $gray-300;
  }

  .meal-card__image:before {
    content: "";
    background-color: rgba($gray-300, 0.7);
  }
}
</style>
